import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { RegistryProvider } from "@effect-atom/atom-react";
import { registerSW } from "virtual:pwa-register";
import { routeTree } from "@/routeTree.gen";
import { env } from "@/shared/config/env";
import { AnatomyModel } from "@/entities/muscle/ui/anatomy-model";
import { HeartRateProvider } from "@/entities/heart-rate/lib/heart-rate-context";
import { notifyUpdateAvailable } from "@/features/pwa-update/ui/pwa-update-banner";
// eslint-disable-next-line import/no-unassigned-import -- global stylesheet side effect
import "./styles/globals.css";

async function purgeStaleServiceWorker(): Promise<void> {
  const registrations = await navigator.serviceWorker.getRegistrations();
  if (registrations.length === 0) return;
  await Promise.all(registrations.map((registration) => registration.unregister()));
  if ("caches" in self) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
  location.reload();
}

if (import.meta.env.DEV && "serviceWorker" in navigator) {
  void purgeStaleServiceWorker();
}

const updateSW = import.meta.env.PROD
  ? registerSW({
      immediate: true,
      onNeedRefresh(): void {
        notifyUpdateAvailable(updateSW);
      },
    })
  : (): Promise<void> => Promise.resolve();
AnatomyModel.preload("/zanatomy_muscular.glb");

const convex = new ConvexReactClient(env.VITE_CONVEX_URL);
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadDelay: 50,
  defaultViewTransition: true,
});

declare module "@tanstack/react-router" {
  // oxlint-disable-next-line typescript/consistent-type-definitions -- declaration merging requires interface
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

createRoot(rootEl).render(
  <ConvexProvider client={convex}>
    <RegistryProvider>
      <HeartRateProvider>
        <RouterProvider router={router} />
      </HeartRateProvider>
    </RegistryProvider>
  </ConvexProvider>,
);
